/**
 * @class BaseInProductPackage
 */
export default class BaseInProductPackage {
    /**
     * @param {Number} id
     * @param {String} displayName
     * @param {Number} filling
     * @param {Number} fillingMl
     * @param {Object} product
     * @param {Object} base
     * @param {Object} pack
     */
    constructor(id, displayName, filling, fillingMl, product, base, pack) {
        this._id = id;
        this._displayName = displayName;
        this._filling = filling;
        this._fillingMl = fillingMl;
        this._product = product;
        this._base = base;
        this._package = pack;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - BaseInProductPackage");
        }

        if (this._product === null || this._base === null || this._package === null) {
            throw new Error("Invalid BaseInProductPackage values");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get displayName() {
        return this._displayName;
    }

    /**
     * @param {String} displayName
     */
    set displayName(displayName) {
        this._displayName = displayName;
    }

    /**
     * @return {Number}
     */
    get filling() {
        return this._filling;
    }

    /**
     * @param {Number} filling
     */
    set filling(filling) {
        this._filling = filling;
    }

    /**
     * @return {Number}
     */
    get fillingMl() {
        return this._fillingMl;
    }

    /**
     * @param {Number} fillingMl
     */
    set fillingMl(fillingMl) {
        this._fillingMl = fillingMl;
    }

    /**
     * @return {Object}
     */
    get product() {
        return this._product;
    }

    /**
     * @param {Object} product
     */
    set productId(product) {
        this._product = product;
    }

    /**
     * @return {Object}
     */
    get base() {
        return this._base;
    }

    /**
     * @param {Object} base
     */
    set base(base) {
        this._base = base;
    }

    /**
     * @return {Object}
     */
    get package() {
        return this._package;
    }

    /**
     * @param {Object} package
     */
    set package(pack) {
        this._package = pack;
    }

    /**
     * Clones BIPP object and updates package property.
     *
     * @param   {Object}    pack
     * @return  {Object}
     */
    updatePackage = (pack) => {
        return new BaseInProductPackage(
            0,
            pack.displayName,
            pack.filling,
            pack.fillingMl,
            this._product,
            this._base,
            pack
        );
    };

    /**
     * Clones BIPP object and updates fillingMl property.
     *
     * @param   {Number}    fillingMl
     * @return  {Object}
     */
    updatePackageFillingMl = (fillingMl) => {
        return this.updatePackage(this._package.updateFillingMl(fillingMl));
    };

    /**
     * @return {String}
     */
    get name() {
        return this._package._name;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._package.name = name;
    }

    /**
     * @return {Number}
     */
    get size() {
        return this._package.nominalAmount;
    }

    /**
     * @return {Number}
     */
    get unit() {
        return this._package.unit;
    }

    /**
     * @param {Number} unit
     */
    set unit(unit) {
        this._package.unit = unit;
    }

    /**
     * @return {String}
     */
    get unitCode() {
        return this._package.unitCode;
    }

    /**
     * @param {String} unitCode
     */
    set unitCode(unitCode) {
        this._package.unitCode = unitCode;
    }

    /**
     * @return {Number}
     */
    get nominalAmount() {
        return this._package.nominalAmount;
    }

    /**
     * @param {Number} nominalAmount
     */
    set nominalAmount(nominalAmount) {
        this._package.nominalAmount = nominalAmount;
    }

    /**
     * @return {Boolean}
     */
    get isGravimetric() {
        return this._package.isGravimetric;
    }

    /**
     * @param {Boolean} isGravimetric
     */
    set isGravimetric(isGravimetric) {
        this._package.isGravimetric = isGravimetric;
    }

    /**
     * @return {Number}
     */
    get imageId() {
        return this._package.imageId;
    }

    /**
     * @param {Number}  imageId
     */
    set imageId(imageId) {
        this._package.imageId = imageId;
    }

    /**
     * Clones Package object and updates imageId property.
     *
     * @param   {Number}    imageId
     * @return  {Object}
     */
    updateImageId = (imageId) => {
        return new BaseInProductPackage(
            this._id,
            this._displayName,
            this._filling,
            this._fillingMl,
            this._product,
            this._base,
            this._package.updateImageId(imageId)
        );
    };

    /**
     * @return {Boolean}
     */
    isCustom = () => {
        return this._package.id <= 0;
    };

    /**
     * @return {Boolean}
     */
    isSimplePackage = () => {
        return this._displayName === this._package.displayName;
    };

    /**
     * @return {String}
     */
    getLongDisplayName = () => {
        if (this.isSimplePackage()) {
            return this._displayName + "  " + this._base.displayName + "  " + this._product.displayName;
        }

        return (
            this._displayName +
            "   " +
            this._base.displayName +
            "   " +
            this._product.displayName +
            "   " +
            this._package.displayName
        );
    };
}
